import * as React from "react";
import { WorkflowNode } from "../../types";
import { Divider, FormGroup, ToggleGroup } from "../RightAsideHelpers";
import Input from "@/components/Input";
import TextArea from "@/components/TextArea";
import Select from "@/components/Select";
import { useMemo, useState } from "react";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

type RcsMessageType = "text" | "richCard" | "carousel" | "suggestedReplies";

interface RcsCardAction {
  type: string;
  label: string;
  value: string;
}

interface RcsCard {
  title: string;
  description: string;
  imageUrl: string;
  actions: RcsCardAction[];
}

export default function SendRcsEditor({ node, onUpdate }: Props) {
  const [activeCardIndex, setActiveCardIndex] = useState(0);

  const messageType: RcsMessageType = (node.rcsMessageType as RcsMessageType) || "text";

  const phoneSourceOptions = useMemo(() => ({
    callerID: "Caller's Phone Number (Safest)",
    attribute: "Collected Phone Number",
    fallback: "Use Email as Fallback"
  }), []);

  const fallbackActionOptions = useMemo(() => ({
    skipRcs: "Skip & Continue",
    fallbackSms: "Fall Back to SMS",
    pauseWorkflow: "Pause Workflow"
  }), []);

  const messageTypeOptions = useMemo(() => ({
    text: "Text Message (with optional suggested replies)",
    richCard: "Rich Card (image + title + buttons)",
    carousel: "Carousel (2-10 scrollable cards)",
    suggestedReplies: "Suggested Replies Only (quick chips)"
  }), []);

  const actionTypeOptions = useMemo(() => ({
    openUrl: "Open URL",
    dialPhone: "Dial Phone Number",
    mapLocation: "Open Map Location",
    reply: "Quick Reply Text"
  }), []);

  // Safe access for cards array
  const cards: RcsCard[] = Array.isArray(node.rcsCards) ? node.rcsCards : [];
  const suggestedReplies: string[] = Array.isArray(node.rcsSuggestedReplies) ? node.rcsSuggestedReplies : [];

  const updateCard = (index: number, field: keyof RcsCard, value: any) => {
    const newCards = [...cards];
    if (!newCards[index]) {
      newCards[index] = { title: "", description: "", imageUrl: "", actions: [] };
    }
    newCards[index] = { ...newCards[index], [field]: value };
    onUpdate({ rcsCards: newCards });
  };

  const addCard = () => {
    const newCards = [...cards, { title: "", description: "", imageUrl: "", actions: [] }];
    onUpdate({ rcsCards: newCards });
    setActiveCardIndex(newCards.length - 1);
  };

  const removeCard = (index: number) => {
    const newCards = cards.filter((_, i) => i !== index);
    onUpdate({ rcsCards: newCards });
    if (activeCardIndex >= newCards.length) {
      setActiveCardIndex(Math.max(0, newCards.length - 1));
    }
  };

  const addAction = (cardIndex: number) => {
    const newCards = [...cards];
    if (!newCards[cardIndex]) return;
    const actions = [...(newCards[cardIndex].actions || [])];
    if (actions.length >= 4) return; // RCS max 4 actions per card
    actions.push({ type: "openUrl", label: "", value: "" });
    newCards[cardIndex] = { ...newCards[cardIndex], actions };
    onUpdate({ rcsCards: newCards });
  };

  const updateAction = (cardIndex: number, actionIndex: number, field: keyof RcsCardAction, value: string) => {
    const newCards = [...cards];
    const actions = [...(newCards[cardIndex]?.actions || [])];
    actions[actionIndex] = { ...actions[actionIndex], [field]: value };
    newCards[cardIndex] = { ...newCards[cardIndex], actions };
    onUpdate({ rcsCards: newCards });
  };

  const removeAction = (cardIndex: number, actionIndex: number) => {
    const newCards = [...cards];
    const actions = (newCards[cardIndex]?.actions || []).filter((_, i) => i !== actionIndex);
    newCards[cardIndex] = { ...newCards[cardIndex], actions };
    onUpdate({ rcsCards: newCards });
  };

  const addSuggestedReply = () => {
    if (suggestedReplies.length >= 11) return; // RCS max 11 suggestions
    onUpdate({ rcsSuggestedReplies: [...suggestedReplies, ""] });
  };

  const updateSuggestedReply = (index: number, value: string) => {
    const newReplies = [...suggestedReplies];
    newReplies[index] = value;
    onUpdate({ rcsSuggestedReplies: newReplies });
  };

  const removeSuggestedReply = (index: number) => {
    onUpdate({ rcsSuggestedReplies: suggestedReplies.filter((_, i) => i !== index) });
  };

  return (
    <>
      <div className="bg-indigo-50 rounded-lg p-3 mb-4 text-xs text-indigo-800">
        <i className="fas fa-comment-dots text-indigo-500 mr-1" aria-hidden="true" />
        <strong>RCS Message:</strong> Rich messages with images, buttons, and carousels.
        Auto-falls back to SMS if the recipient's device doesn't support RCS.
      </div>

      <FormGroup
        label="Phone Number Source"
        tooltipText="Choose where to get the recipient's phone number"
      >
        <Select
          options={phoneSourceOptions}
          value={node.phoneSource || "callerID"}
          onChange={(phoneSource) => onUpdate({ phoneSource: phoneSource as any })}
        />
      </FormGroup>

      {node.phoneSource === "attribute" && (
        <FormGroup
          label="Contact/Conversation Attribute"
          tooltipText="Name of the attribute containing the phone number"
        >
          <Input
            type="text"
            placeholder="e.g., collectedPhone"
            value={node.phoneAttribute || ""}
            onChange={(phoneAttribute) => onUpdate({ phoneAttribute })}
          />
        </FormGroup>
      )}

      <Divider />

      <FormGroup
        label="Message Type"
        tooltipText="Choose the type of RCS message to send"
      >
        <Select
          options={messageTypeOptions}
          value={messageType}
          onChange={(rcsMessageType) => onUpdate({ rcsMessageType })}
        />
      </FormGroup>

      {/* Text message body — for text and suggestedReplies types */}
      {(messageType === "text" || messageType === "suggestedReplies") && (
        <>
          <FormGroup label="Message Text">
            <TextArea
              value={node.rcsMessage || ""}
              onChange={(e) => onUpdate({ rcsMessage: e.currentTarget.value })}
              placeholder="Enter your message. Supports {{variable}} syntax. Up to 8,000 characters."
            />
            <div className="text-xs text-slate-400 mt-1">
              {(node.rcsMessage?.length ?? 0).toLocaleString()} / 8,000 characters
            </div>
          </FormGroup>

          <div className="text-sm text-slate-600 mb-4">
            <div className="font-medium mb-2">Available Variables:</div>
            <div className="space-y-1 text-xs">
              <div><strong>System:</strong> {"{{callerPhoneNumber}}, {{siteId}}, {{conversationId}}"}</div>
              <div><strong>Contact:</strong> {"{{firstName}}, {{lastName}}, {{email}}, {{phone}}"}</div>
              <div><strong>Conversation:</strong> {"{{collectedPhone}}, {{collectedEmail}}, {{orderNumber}}, {{ticketId}}"}</div>
              <div><strong>Workflow:</strong> {"{{otp}}, {{authCode}}, {{verificationLink}}"}</div>
            </div>
          </div>
        </>
      )}

      {/* Rich Card editor */}
      {messageType === "richCard" && (
        <>
          <div className="text-sm font-medium text-slate-700 mb-2">Rich Card</div>
          <CardEditor
            card={cards[0] || { title: "", description: "", imageUrl: "", actions: [] }}
            onChange={(field, value) => updateCard(0, field, value)}
            actionTypeOptions={actionTypeOptions}
            onAddAction={() => addAction(0)}
            onUpdateAction={(ai, field, value) => updateAction(0, ai, field, value)}
            onRemoveAction={(ai) => removeAction(0, ai)}
          />
        </>
      )}

      {/* Carousel editor */}
      {messageType === "carousel" && (
        <>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">
              Carousel Cards ({cards.length}/10)
            </span>
            {cards.length < 10 && (
              <button
                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                onClick={addCard}
                type="button"
              >
                + Add Card
              </button>
            )}
          </div>

          {cards.length === 0 && (
            <div className="text-center py-6 bg-slate-50 rounded-lg border border-dashed border-slate-200 mb-4">
              <p className="text-slate-400 text-sm">No cards yet. Add at least 2 cards for a carousel.</p>
              <button
                className="text-sm text-indigo-600 hover:text-indigo-800 font-medium mt-2"
                onClick={addCard}
                type="button"
              >
                + Add First Card
              </button>
            </div>
          )}

          {cards.length > 0 && (
            <>
              {/* Card tabs */}
              <div className="flex gap-1 mb-3 flex-wrap">
                {cards.map((_, i) => (
                  <button
                    key={i}
                    className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                      activeCardIndex === i
                        ? "bg-indigo-600 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                    onClick={() => setActiveCardIndex(i)}
                    type="button"
                  >
                    Card {i + 1}
                  </button>
                ))}
              </div>

              {/* Active card editor */}
              {cards[activeCardIndex] && (
                <div className="relative">
                  {cards.length > 2 && (
                    <button
                      className="absolute top-0 right-0 text-xs text-red-500 hover:text-red-700"
                      onClick={() => removeCard(activeCardIndex)}
                      type="button"
                      aria-label={`Remove card ${activeCardIndex + 1}`}
                    >
                      Remove
                    </button>
                  )}
                  <CardEditor
                    card={cards[activeCardIndex]}
                    onChange={(field, value) => updateCard(activeCardIndex, field, value)}
                    actionTypeOptions={actionTypeOptions}
                    onAddAction={() => addAction(activeCardIndex)}
                    onUpdateAction={(ai, field, value) => updateAction(activeCardIndex, ai, field, value)}
                    onRemoveAction={(ai) => removeAction(activeCardIndex, ai)}
                  />
                </div>
              )}

              {cards.length < 2 && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-2 text-xs text-amber-800 mt-2">
                  <i className="fas fa-info-circle text-amber-500 mr-1" aria-hidden="true" />
                  Carousels require at least 2 cards. Add another card to complete the carousel.
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Suggested Replies — available on text and suggestedReplies types */}
      {(messageType === "text" || messageType === "suggestedReplies") && (
        <>
          <Divider />
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">
              Suggested Replies ({suggestedReplies.length}/11)
            </span>
            {suggestedReplies.length < 11 && (
              <button
                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                onClick={addSuggestedReply}
                type="button"
              >
                + Add Chip
              </button>
            )}
          </div>
          <p className="text-xs text-slate-400 mb-3">
            Quick-action buttons shown below the message. Recipient taps one to reply instantly.
          </p>
          {suggestedReplies.map((reply, i) => (
            <div key={i} className="flex items-center gap-2 mb-2">
              <input
                type="text"
                className="flex-1 rounded-md border-slate-300 shadow-sm text-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder={`Reply option ${i + 1}`}
                value={reply}
                onChange={(e) => updateSuggestedReply(i, e.target.value)}
                maxLength={25}
              />
              <button
                className="text-red-400 hover:text-red-600 text-xs flex-shrink-0"
                onClick={() => removeSuggestedReply(i)}
                type="button"
                aria-label={`Remove reply ${i + 1}`}
              >
                <i className="fas fa-xmark" aria-hidden="true" />
              </button>
            </div>
          ))}
        </>
      )}

      <Divider />

      {/* Fallback settings */}
      <ToggleGroup
        toggled={node.rcsSmsFallback !== false}
        label="SMS Fallback"
        helpText="If RCS delivery fails (recipient doesn't support RCS), automatically re-send as a standard SMS text message."
        changeToggle={() => onUpdate({ rcsSmsFallback: node.rcsSmsFallback === false })}
      />

      {node.rcsSmsFallback === false && (
        <FormGroup
          label="If RCS Not Available"
          tooltipText="What to do when the recipient doesn't support RCS and fallback is disabled"
        >
          <Select
            options={fallbackActionOptions}
            value={node.fallbackAction || "fallbackSms"}
            onChange={(fallbackAction) => onUpdate({ fallbackAction: fallbackAction as any })}
          />
        </FormGroup>
      )}

      <Divider />

      <FormGroup
        label="Max Messages Per Day"
        tooltipText="Prevent spam by limiting RCS messages to the same recipient per day"
      >
        <Input
          type="number"
          value={node.dailyLimit || ""}
          onChange={(dailyLimit) => onUpdate({ dailyLimit: dailyLimit ? Number(dailyLimit) : undefined })}
          placeholder="e.g., 5 (blank = unlimited)"
        />
      </FormGroup>

      <ToggleGroup
        toggled={node.enableTranscript || false}
        label="Log in Conversation Transcript"
        helpText="Record the RCS message in the conversation transcript for compliance and audit."
        changeToggle={() => onUpdate({ enableTranscript: !node.enableTranscript })}
      />

      <ToggleGroup
        toggled={node.trackDelivery || false}
        label="Track Delivery & Read Receipts"
        helpText="Store delivery and read receipt timestamps. Visible in conversation details and reports."
        changeToggle={() => onUpdate({ trackDelivery: !node.trackDelivery })}
      />
    </>
  );
}

// ── Card Editor (shared between richCard and carousel) ──────────────────────

function CardEditor({
  card,
  onChange,
  actionTypeOptions,
  onAddAction,
  onUpdateAction,
  onRemoveAction
}: {
  card: RcsCard;
  onChange: (field: keyof RcsCard, value: any) => void;
  actionTypeOptions: Record<string, string>;
  onAddAction: () => void;
  onUpdateAction: (actionIndex: number, field: keyof RcsCardAction, value: string) => void;
  onRemoveAction: (actionIndex: number) => void;
}) {
  return (
    <div className="bg-slate-50 rounded-lg p-4 mb-4 space-y-3">
      {/* Preview */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden max-w-xs">
        {card.imageUrl && (
          <div className="h-32 bg-slate-100 flex items-center justify-center">
            <img
              src={card.imageUrl}
              alt="Card preview"
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          </div>
        )}
        <div className="p-3">
          <div className="font-medium text-sm text-slate-800">{card.title || "Card Title"}</div>
          <div className="text-xs text-slate-500 mt-1">{card.description || "Card description..."}</div>
        </div>
      </div>

      <FormGroup label="Image URL">
        <Input
          type="text"
          placeholder="https://example.com/image.jpg"
          value={card.imageUrl}
          onChange={(imageUrl) => onChange("imageUrl", imageUrl)}
        />
        <p className="text-xs text-slate-400 mt-1">JPEG or PNG, max 2MB. Recommended 1440x720px.</p>
      </FormGroup>

      <FormGroup label="Title">
        <Input
          type="text"
          placeholder="Product name, offer headline, etc."
          value={card.title}
          onChange={(title) => onChange("title", title)}
        />
      </FormGroup>

      <FormGroup label="Description">
        <TextArea
          value={card.description}
          onChange={(e) => onChange("description", e.currentTarget.value)}
          placeholder="Brief description (max 2,000 chars)"
        />
      </FormGroup>

      {/* Action buttons */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-600">
          Action Buttons ({card.actions?.length ?? 0}/4)
        </span>
        {(card.actions?.length ?? 0) < 4 && (
          <button
            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
            onClick={onAddAction}
            type="button"
          >
            + Add Button
          </button>
        )}
      </div>

      {card.actions?.map((action, i) => (
        <div key={i} className="bg-white rounded border border-slate-200 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500">Button {i + 1}</span>
            <button
              className="text-xs text-red-400 hover:text-red-600"
              onClick={() => onRemoveAction(i)}
              type="button"
              aria-label={`Remove button ${i + 1}`}
            >
              Remove
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Type</label>
              <Select
                options={actionTypeOptions}
                value={action.type}
                onChange={(type) => onUpdateAction(i, "type", type)}
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Label</label>
              <Input
                type="text"
                placeholder="Button text"
                value={action.label}
                onChange={(label) => onUpdateAction(i, "label", label)}
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">
              {action.type === "openUrl" ? "URL" :
               action.type === "dialPhone" ? "Phone Number" :
               action.type === "mapLocation" ? "Location (lat,lng or address)" :
               "Reply Text"}
            </label>
            <Input
              type="text"
              placeholder={
                action.type === "openUrl" ? "https://example.com" :
                action.type === "dialPhone" ? "+15551234567" :
                action.type === "mapLocation" ? "37.7749,-122.4194" :
                "User's reply text"
              }
              value={action.value}
              onChange={(value) => onUpdateAction(i, "value", value)}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
